import { useRef, useState, useEffect } from "react";
import { Mic, Square } from "lucide-react";
import { toast } from "sonner";

/**
 * A small microphone button that dictates speech into a controlled text field.
 * Uses the browser's Web Speech API (Chrome/Edge/Safari). If unsupported, it
 * renders nothing so typing still works everywhere.
 *
 * Props:
 *  - value: current field value
 *  - onChange: (newValue) => void  (called with dictated text appended)
 *  - className: extra classes for positioning
 */
export default function MicButton({ value, onChange, className = "", testId }) {
  const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const baseRef = useRef("");

  useEffect(() => {
    return () => {
      try {
        recRef.current?.stop();
      } catch (e) {
        /* noop */
      }
    };
  }, []);

  if (!SR) return null;

  const stop = () => {
    try {
      recRef.current?.stop();
    } catch (e) {
      /* noop */
    }
    setListening(false);
  };

  const start = () => {
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;
    baseRef.current = (value || "").trim();
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript + " ";
        else interim += r[0].transcript;
      }
      const dictated = (finalText + interim).trim();
      const base = baseRef.current;
      onChange(base ? `${base} ${dictated}` : dictated);
    };
    rec.onerror = (ev) => {
      setListening(false);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        toast.error("Please allow microphone access to dictate.");
      }
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    try {
      rec.start();
    } catch (e) {
      setListening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      data-testid={testId}
      aria-pressed={listening}
      title={listening ? "Stop dictating" : "Dictate with your voice"}
      className={`inline-flex items-center justify-center h-8 w-8 rounded-full border transition-all ${
        listening
          ? "bg-[#B27A70] border-[#B27A70] text-white animate-pulse"
          : "bg-white border-[#E8E3D9] text-[#6E6860] hover:text-[#4A5D4E] hover:border-[#4A5D4E]/50"
      } ${className}`}
    >
      {listening ? <Square className="h-3.5 w-3.5" fill="currentColor" /> : <Mic className="h-4 w-4" strokeWidth={1.75} />}
    </button>
  );
}
